import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { CheckCircle, ChevronRight, ChevronLeft, Building, User, FileText, CreditCard, Loader2, Zap, Check, ClipboardList, Lock, Calendar, Star, AlertTriangle, XCircle, Construction, PartyPopper } from 'lucide-react';
import { API_URL } from '../config';
import Header from './Header';
import Footer from './Footer';
import Pricing from './Pricing';
import UseCases from './UseCases';
import About from './About';
import FAQ from './FAQ';
import SeoContent from './SeoContent';
import homepageSchema from '../data/homepage-schema.json';
import { trackEvent, buildItem, savePendingPurchase } from '../utils/analytics';
import { getAttribution } from '../utils/attribution';

// JSON-LD Schema e in src/data/homepage-schema.json (randat in Helmet doar pe homepage)
// Functia ramane comentata pentru referinta
/*
const getSchemaMarkup_MOVED_TO_INDEX_HTML = () => {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": "https://constatatoare.ro/#organization",
        "name": "Constatatoare.ro - BITCRAFTERS SRL",
        "description": "Platformă online pentru obținerea certificatelor constatator de la Registrul Comerțului. Procesare rapidă în 5 minute, livrare pe email.",
        "url": "https://constatatoare.ro",
        "logo": "https://constatatoare.ro/logo-full.png",
        "image": "https://constatatoare.ro/og-image-v2.jpg",
        "telephone": "+40756888282",
        "email": "contact@constatatoare.ro",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Str. Fratii Golescu 4",
          "addressLocality": "București",
          "addressRegion": "Sector 1",
          "postalCode": "010482",
          "addressCountry": "RO"
        },
        "priceRange": "99-449 RON",
        "sameAs": [
          "https://share.google/Af2LoButXUbxIwoM1"
        ],
        "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          "opens": "00:00",
          "closes": "23:59"
        }
      },
      {
        "@type": "Service",
        "@id": "https://constatatoare.ro/#service",
        "name": "Certificat Constatator Online",
        "description": "Obține certificat constatator de la ONRC 100% online, fără deplasare. Plată cu cardul, livrare pe email în 5-15 minute.",
        "provider": { "@id": "https://constatatoare.ro/#organization" },
        "serviceType": "Certificat Constatator ONRC",
        "areaServed": {
          "@type": "Country",
          "name": "România"
        },
        "offers": [
          {
            "@type": "Offer",
            "name": "Certificat Constatator",
            "price": "99",
            "priceCurrency": "RON",
            "description": "Certificat constatator standard cu semnătură electronică"
          },
          {
            "@type": "Offer",
            "name": "Certificat cu Istoric",
            "price": "449",
            "priceCurrency": "RON",
            "description": "Certificat constatator cu istoric complet al firmei"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "@id": "https://constatatoare.ro/#faq",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Ce este certificatul constatator?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Certificatul constatator este un document oficial emis de Oficiul Național al Registrului Comerțului (ONRC) care atestă informațiile actuale despre o firmă: denumire, sediu social, asociați, administratori, capital social, obiect de activitate și starea firmei."
            }
          },
          {
            "@type": "Question",
            "name": "Cât durează să primesc certificatul constatator?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Certificatul este emis automat după confirmarea plății. Procesul poate dura de la 5 până la 15 minute, iar documentul este trimis imediat pe email în format PDF."
            }
          },
          {
            "@type": "Question",
            "name": "Certificatele sunt oficiale și valabile legal?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "DA, 100% oficiale! Certificatele sunt descărcate direct din portalul ONRC (Oficiul Național al Registrului Comerțului) și conțin ștampila electronică oficială. Sunt valabile atât pentru persoane juridice cât și pentru persoane fizice."
            }
          },
          {
            "@type": "Question",
            "name": "Ce documente am nevoie pentru comandă?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ai nevoie doar de CUI-ul (Codul Unic de Identificare) al firmei pentru care dorești certificatul. Restul datelor sunt completate automat din baza de date ONRC."
            }
          },
          {
            "@type": "Question",
            "name": "Care este valabilitatea certificatului constatator?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Certificatul constatator nu are o valabilitate fixă stabilită prin lege. Însă, majoritatea instituțiilor acceptă certificate emise în ultimele 30 de zile. Pentru licitații publice, verifică cerințele specifice din documentația de atribuire."
            }
          },
          {
            "@type": "Question",
            "name": "Ce metode de plată acceptați?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Acceptăm plata cu card bancar (Visa, Mastercard, Maestro) prin intermediul platformei Stripe, lider global în securitatea plăților online. Toate tranzacțiile sunt criptate SSL și procesate în conformitate cu standardele PCI-DSS."
            }
          },
          {
            "@type": "Question",
            "name": "Pot solicita certificat pentru orice persoană juridică sau fizică din România?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "DA, pentru toate tipurile de persoane juridice sau fizice care sunt existente la Registrul Comerțului! Platforma noastră automatizează procesul de obținere a certificatelor. Nu contează unde este înregistrată firma – certificatul este emis automat."
            }
          },
          {
            "@type": "Question",
            "name": "Primesc factură fiscală pentru plată?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Da, veți primi pe mail factura fiscală."
            }
          },
          {
            "@type": "Question",
            "name": "Ce se întâmplă dacă introduc date greșite (CUI/CNP)?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Platforma noastră validează automat datele introduse înainte de a permite plata. Dacă CUI-ul sau CNP-ul nu sunt valide sau nu corespund cu datele existente în Registrul Comerțului, veți primi eroare instant și nu veți putea continua procesul. Astfel, nu există risc să plătiți pentru un certificat incorect."
            }
          },
          {
            "@type": "Question",
            "name": "Sunteți ONRC oficial? De ce să cer certificatul prin voi?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "NU, nu suntem instituție guvernamentală. Suntem o platformă software care automatizează procesul de obținere a certificatelor. Avantajele sunt: Fără cont necesar – Noi facem tot procesul pentru dvs. Fără așteptare – Certificat în 5-15 minute. Fără deplasare – Tot procesul 100% online."
            }
          },
          {
            "@type": "Question",
            "name": "Certificatul primit este document original?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Da, certificatul primit este documentul oficial emis de ONRC, semnat electronic cu semnătură calificată. Are aceeași valoare juridică ca și cel obținut la ghișeu."
            }
          },
          {
            "@type": "Question",
            "name": "Oferiți suport pentru întrebări suplimentare?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "DA, suport complet! Telefon: +40 756 888 282. Email: contact@constatatoare.ro. Echipa noastră vă ajută cu orice problemă legată de comandă, plată sau descărcarea certificatului."
            }
          }
        ]
      }
    ]
  };
};
*/

// Toast Notification Component (floating bottom - elegant pill design)
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000); // Auto-close după 5s
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-24 left-1/2 z-50
                     px-5 py-3 rounded-full shadow-lg text-white text-sm font-medium
                     flex items-center gap-2 backdrop-blur-sm
                     transition-all duration-300 ease-out
                     animate-fade-in
                     ${type === 'error' ? 'bg-red-500/95' : 'bg-emerald-500/95'}`}
         style={{ transform: 'translateX(-50%)', willChange: 'transform, opacity' }}>
      {type === 'error' ? <XCircle className="w-5 h-5 flex-shrink-0" /> : <CheckCircle className="w-5 h-5 flex-shrink-0" />}
      <span className="max-w-xs sm:max-w-md">{message}</span>
    </div>
  );
};

const CertificateWizard = () => {
  const [step, setStep] = useState(1);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Toast notification state (înlocuiește error/success)
  const [toast, setToast] = useState(null);

  // Flash vizual pentru ghidare UX (fără auto-focus)
  const [flashElement, setFlashElement] = useState(null);

  // Ref pentru progress bar (mobile scroll după Continuare)
  const progressBarRef = useRef(null);
  // Ref pentru buton Continuare (mobile scroll după selectare certificat)
  const continueButtonRef = useRef(null);
  // Ref pentru buton Plătește (mobile scroll la step 5)
  const paymentButtonRef = useRef(null);

  // Încarcă prețuri INSTANT din localStorage (elimină flash complet!)
  const [prices, setPrices] = useState(() => {
    const cached = localStorage.getItem('onrc_prices');
    return cached ? JSON.parse(cached) : { basic: 99.01, withHistory: 449 }; // Fallback (apare si in HTML-ul pre-randat de react-snap)
  });

  const [formData, setFormData] = useState({
    userType: 'company', // Preselectat Persoană Juridică
    cui: '',
    cnp: '',
    companyData: null,
    certificateType: '',
    certificatePurpose: 'Informare', // Default "Informare" pentru a reduce fricțiunea
    billingType: '',
    billingData: {},
    contactData: { fullName: '', email: '', phone: '' }
  });
  const [isVerifyingCUI, setIsVerifyingCUI] = useState(false);
  const [isVerifyingCNP, setIsVerifyingCNP] = useState(false);
  const [cnpValidated, setCnpValidated] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false); // FIX #27 - Checkbox Termeni și Condiții (obligatoriu)
  const [acceptedMarketing, setAcceptedMarketing] = useState(false); // FIX #27 - Checkbox comunicări comerciale (opțional)

  // Validare live pentru Step 4 - Date Contact
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [nameError, setNameError] = useState('');

  // Cod de reducere (Faza 3)
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null); // {code, discountAmount, newPrice}
  const [discountError, setDiscountError] = useState('');
  const [discountLoading, setDiscountLoading] = useState(false);
  const [urlDiscount, setUrlDiscount] = useState(null); // reducere pre-completată din ?cod= (email): { code, basic:{amount,newPrice}, withHistory:{amount,newPrice} }
  const [urlCodeError, setUrlCodeError] = useState(null); // cod din URL invalid/inexistent (string, pentru mesaj)
  const [discountDismissed, setDiscountDismissed] = useState(false); // user a apăsat „Elimină"

  // Comenzi nefinalizate (cos abandonat) - 12.10.2026
  const [abandonedCartEnabled, setAbandonedCartEnabled] = useState(false); // pornit din admin
  const [draftToken, setDraftToken] = useState(() => {
    try { return sessionStorage.getItem('order_draft_token') || ''; } catch (e) { return ''; }
  });
  const [resumeNeedsCnp, setResumeNeedsCnp] = useState(false); // PF revenit din email: CNP-ul nu se salveaza, se reintroduce

  useEffect(() => {
    fetchPrices();
    fetchFeatures(); // Check maintenance mode
  }, []);

  // Citește cod de reducere din URL (?cod=SEP20 din email) și îl validează SERVER-SIDE pentru ambele tipuri.
  // Cod fals/expirat → validarea pică → urlDiscount rămâne null → wizard normal, zero efect.
  useEffect(() => {
    const cod = new URLSearchParams(window.location.search).get('cod');
    if (!cod) return;
    (async () => {
      try {
        const types = ['basic', 'withHistory'];
        const results = await Promise.all(types.map(async (t) => {
          const res = await fetch(`${API_URL}/api/orders/validate-discount`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: cod, certificateType: t })
          });
          return { t, data: await res.json() };
        }));
        const dd = {};
        let code = null;
        results.forEach(({ t, data }) => {
          if (data.valid && data.discountAmount > 0) {
            dd[t] = { amount: data.discountAmount, newPrice: data.newPrice };
            code = data.code;
          }
        });
        if (code) setUrlDiscount({ code, ...dd });
        else setUrlCodeError(cod); // cod prezent în URL dar invalid pe ambele tipuri
      } catch (e) { /* silent - wizard-ul merge normal fără reducere */ }
    })();
  }, []);

  // Revenire din emailul "comanda nefinalizata": tokenul a fost scos din URL in resumeToken.js.
  // PJ -> direct la Confirmare. PF -> pasul 1 (CNP-ul nu se salveaza), apoi direct la Confirmare dupa verificare.
  useEffect(() => {
    const token = window.__resumeDraftToken;
    if (!token) return;
    window.__resumeDraftToken = null;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/orders/draft/${token}`);
        if (!res.ok) return; // expirat / finalizat / dezabonat -> wizard normal
        const { data } = await res.json();
        if (!data || !data.certificateType || !data.contactData?.email) return;
        const isCompany = data.userType === 'company' && data.companyData?.name;
        setFormData((f) => ({
          ...f,
          userType: data.userType === 'individual' ? 'individual' : 'company',
          cui: isCompany ? (data.cui || data.companyData.cui || '') : '',
          cnp: '',
          companyData: isCompany ? data.companyData : null,
          certificateType: data.certificateType,
          certificatePurpose: data.certificatePurpose || f.certificatePurpose,
          billingType: data.billingType || '',
          billingData: data.billingData || {},
          contactData: { fullName: '', email: '', phone: '', ...data.contactData }
        }));
        setDraftToken(token);
        try { sessionStorage.setItem('order_draft_token', token); } catch (e) {}
        if (isCompany) {
          setStep(5);
        } else {
          setResumeNeedsCnp(true);
          setStep(1);
        }
      } catch (e) { /* silent - wizard normal */ }
    })();
  }, []);

  // Flash automat la intrare pe Step 2, 3, 4 și 5
  useEffect(() => {
    // Step 2: Auto-selectare "Certificat Constatator" (cel mai popular)
    if (step === 2 && !formData.certificateType) {
      setFormData({ ...formData, certificateType: 'basic' });
      // Flash la dropdown scop după 600ms (3 secunde flash)
      setTimeout(() => triggerFlash('certificate-purpose', 3000), 600);
    }

    // Step 3: Auto-selectare "Firma din constatator" (prima opțiune)
    if (step === 3 && !formData.billingType && formData.companyData) {
      setFormData({
        ...formData,
        billingType: 'sameCompany',
        billingData: {
          type: 'company',
          name: formData.companyData.name,
          cui: formData.companyData.cui,
          regCom: formData.companyData.regCom,
          address: formData.companyData.address
        }
      });
    }

    if (step === 4) {
      // Flash secvențial: Nume (200ms) → Email (1000ms = 200 + 800)
      setTimeout(() => triggerFlash('contact-name-input'), 200);
      setTimeout(() => triggerFlash('contact-email-input'), 1000);
    }
    if (step === 5) {
      setTimeout(() => triggerFlash('terms-checkbox'), 200);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionat: ruleaza doar la schimbarea acestor valori
  }, [step]);

  // Auto-aplică reducerea din URL la pasul 5 (dacă nu a fost eliminată manual)
  useEffect(() => {
    if (step === 5 && urlDiscount && formData.certificateType && !appliedDiscount && !discountDismissed) {
      const d = urlDiscount[formData.certificateType];
      if (d && d.amount > 0) {
        setAppliedDiscount({ code: urlDiscount.code, discountAmount: d.amount, newPrice: d.newPrice });
      }
    }
  }, [step, urlDiscount, formData.certificateType, appliedDiscount, discountDismissed]);

  const fetchPrices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/orders/prices`);
      const data = await response.json();

      // Salvează în localStorage pentru încărcare INSTANT la următoarea vizită
      localStorage.setItem('onrc_prices', JSON.stringify(data));

      // Actualizează state (silent update în background)
      setPrices(data);
    } catch (err) {
      console.error('Error fetching prices:', err);
    }
  };

  const fetchFeatures = async () => {
    try {
      const response = await fetch(`${API_URL}/api/orders/features`);
      const data = await response.json();
      setMaintenanceMode(data.maintenanceMode || false);
      setAbandonedCartEnabled(data.abandonedCartEnabled === true);
    } catch (err) {
      console.error('Error fetching features:', err);
      // Fallback: dacă eroare API, permitem access (fail-open pentru UX)
      setMaintenanceMode(false);
    }
  };

  /**
   * Flash vizual helper - trigger animație pe element
   */
  const triggerFlash = (elementId, duration = 1500) => {
    setFlashElement(elementId);
    setTimeout(() => setFlashElement(null), duration);
  };

  /**
   * FIX #27 - Soft reset la schimbare user type
   * Clear doar toast/loading, PĂSTREAZĂ input-ul (user nu pierde datele)
   */
  const handleUserTypeChange = (newType) => {
    setFormData({ ...formData, userType: newType });
    // Soft reset: clear doar mesaje și loading
    setToast(null);
    setIsVerifyingCUI(false);
    setIsVerifyingCNP(false);
    setCnpValidated(false); // Reset validare CNP când se schimbă userType

    // Flash vizual pe input relevant (100ms delay pentru render)
    setTimeout(() => {
      if (newType === 'company') {
        triggerFlash('cui-input');
      } else if (newType === 'individual') {
        triggerFlash('cnp-input');
      }
    }, 100);

    // Mobile: Scroll la butonul Continuare după selectare
    setTimeout(() => {
      if (window.innerWidth < 1024 && continueButtonRef.current) {
        continueButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 200);
  };

  // Liste scopuri separate pentru Persoană Fizică și Juridică
  const purposesIndividual = [
    'Administrația Finanțelor Publice',
    'Agenția Națională de Administrare Fiscală',
    'Agenţia pentru Finanţarea Investiţiilor Rurale (AFIR)',
    'Autorizare',
    'Eliberare cazier judiciar',
    'Informare',
    'Înregistrare în scopuri de TVA',
    'Poliție',
    'Primãrie'
  ];

  const purposesCompany = [
    'Accesare Fonduri Europene',
    'Administrația Finanțelor Publice',
    'Administraţia Fondului pentru Mediu',
    'Agenția de Plăți și Intervenții în Agricultură',
    'Agenția Națională de Administrare Fiscală',
    'Agenția Națională pentru Ocuparea Forței de Muncă',
    'Agenția Națională pentru Protecția Mediului',
    'Agenția Națională pentru Resurse Minerale',
    'Agenţia pentru Finanţarea Investiţiilor Rurale (AFIR)',
    'Ambasadă',
    'Atestare ANRE',
    'Autoritatea Rutieră Română',
    'Autorizare',
    'Bancă',
    'Banca Națională a României',
    'Birou notar public',
    'Casa Națională de Asigurări de Sănătate',
    'Casa Națională de Pensii',
    'Direcţia Generală a Vămilor',
    'Eliberare cazier judiciar',
    'Informare',
    'Înregistrare în scopuri de TVA',
    'Inspectoratul General pentru Imigrări',
    'Instanță',
    'Leasing',
    'Licitație',
    'Ministerul Economiei, Energiei și Mediului de Afaceri',
    'Ministerul Muncii și Justiţiei Sociale',
    'Obținere viză',
    'Oficiul de Cadastru și Publicitate Imobiliară',
    'Parchet',
    'Poliție',
    'Primãrie',
    'PSIPAN',
    'Registrul Auto Român',
    'Registrul Operatorilor Intracomunitari'
  ];

  /**
   * NOUĂ FUNCȚIE - Verifică CUI prin ListaFirme API
   */
  const handleVerifyCUI = async (cuiToVerify = null, isForBilling = false) => {
    const cui = cuiToVerify || formData.cui;

    if (!cui) {
      setToast({ message: 'Introduceți un CUI valid', type: 'error' });
      return;
    }

    // FIX #27 - Salvăm userType la start pentru a verifica dacă s-a schimbat între timp
    const currentUserType = formData.userType;

    setIsVerifyingCUI(true);
    setToast(null); // Clear orice toast anterior

    try {
      // Folosim noul endpoint ListaFirme
      const response = await fetch(`${API_URL}/api/company/verify/${cui}`);
      const data = await response.json();

      // FIX #27 - Verificăm dacă user-ul a schimbat tab-ul între timp
      if (formData.userType !== currentUserType) {
        console.log('User switched tabs during CUI verification - ignoring result');
        return; // User a schimbat tab-ul, nu afișăm mesajul
      }

      if (data.success && data.company) {
        const companyInfo = {
          name: data.company.name,
          cui: data.company.cui,
          registrationNumber: data.company.registrationNumber,
          address: data.company.address,
          county: data.company.county,
          city: data.company.city,
          phone: data.company.phone?.[0] || '',
          email: data.company.email?.[0] || '',
          isActive: data.company.isActive
        };

        // Toast success
        setToast({ message: 'Datele firmei au fost găsite cu succes!', type: 'success' });

        // Dacă e pentru facturare (Step 3)
        if (isForBilling) {
          setFormData({
            ...formData,
            billingData: {
              type: 'company',
              name: companyInfo.name,
              cui: companyInfo.cui,
              registrationNumber: companyInfo.registrationNumber,
              address: companyInfo.address,
              county: companyInfo.county,
              city: companyInfo.city
            }
          });
        } else {
          // Dacă e pentru comanda principală (Step 1)
          setFormData({
            ...formData,
            companyData: companyInfo
          });
          // Mobile: Scroll la butonul Continuare după verificare CUI
          setTimeout(() => {
            if (window.innerWidth < 1024 && continueButtonRef.current) {
              continueButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 200);
        }

      } else {
        setToast({ message: data.error || 'CUI invalid sau firmă negăsită în baza de date', type: 'error' });
      }
    } catch (err) {
      console.error('Error verifying CUI:', err);
      setToast({ message: 'Eroare la verificarea CUI. Verificați conexiunea la internet.', type: 'error' });
    } finally {
      setIsVerifyingCUI(false);
    }
  };

  /**
   * Verifică CNP folosind botul ONRC Playwright
   */
  const handleVerifyCNP = async () => {
    if (!formData.cnp || formData.cnp.length !== 13) {
      setToast({ message: 'CNP-ul trebuie să conțină exact 13 cifre', type: 'error' });
      return;
    }

    // FIX #27 - Salvăm userType la start pentru a verifica dacă s-a schimbat între timp
    const currentUserType = formData.userType;

    setIsVerifyingCNP(true);
    setToast(null); // Clear orice toast anterior

    try {
      const response = await fetch(`${API_URL}/api/onrc/cnp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cnp: formData.cnp,
          useBot: true  // Activează bot ONRC pentru verificare reală
        })
      });

      const data = await response.json();

      // FIX #27 - Verificăm dacă user-ul a schimbat tab-ul între timp
      if (formData.userType !== currentUserType) {
        console.log('User switched tabs during CNP verification - ignoring result');
        return; // User a schimbat tab-ul, nu afișăm mesajul
      }

      if (data.success) {
        setToast({ message: data.message || 'CNP-ul a fost identificat cu succes în Registrul Comerțului.', type: 'success' });
        setCnpValidated(true);
        if (resumeNeedsCnp) { // revenit din email: restul datelor sunt deja completate
          setResumeNeedsCnp(false);
          setStep(5);
          return;
        }
        // Mobile: Scroll la butonul Continuare după verificare CNP
        setTimeout(() => {
          if (window.innerWidth < 1024 && continueButtonRef.current) {
            continueButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 200);
      } else {
        setToast({ message: data.message || data.error || 'CNP invalid sau neidentificat în Registrul Comerțului', type: 'error' });
      }
    } catch (err) {
      setToast({ message: 'Eroare la verificarea CNP-ului. Vă rugăm să încercați din nou.', type: 'error' });
    } finally {
      setIsVerifyingCNP(false);
    }
  };

  /**
   * Validare live pentru Nume (Step 4)
   */
  const validateNameLive = (name) => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      setNameError('');
      return;
    }

    // Regex: Doar litere (inclusiv diacritice), spații și cratimă
    const validCharsRegex = /^[a-zA-ZăâîșțĂÂÎȘȚ\s-]+$/;

    // Verificare caractere invalide (cifre, puncte, @, #, etc.)
    if (!validCharsRegex.test(trimmedName)) {
      setNameError('Doar litere, spații și cratimă (-)');
      return;
    }

    // Split pe spații și elimină elemente goale
    const words = trimmedName.split(/\s+/).filter(w => w.length > 0);

    // Verificare minim 2 cuvinte (Nume + Prenume)
    if (words.length < 2) {
      setNameError('Introduceți Nume și Prenume (minim 2 cuvinte)');
      return;
    }

    // Verificare fiecare cuvânt să aibă minim 3 caractere
    const invalidWord = words.find(w => w.length < 3);
    if (invalidWord) {
      setNameError('Fiecare cuvânt trebuie să aibă minim 3 caractere');
      return;
    }

    setNameError(''); // Valid!
  };

  /**
   * Validare live pentru Email (Step 4)
   */
  const validateEmailLive = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!email) {
      setEmailError('');
      return;
    }

    if (email.includes('..')) {
      setEmailError('Email invalid: puncte duble consecutive');
    } else if (email.includes(' ')) {
      setEmailError('Email invalid: nu poate conține spații');
    } else if (!emailRegex.test(email)) {
      setEmailError('Email invalid: format incorect');
    } else {
      setEmailError(''); // Valid!
    }
  };

  /**
   * Validare live pentru Telefon (Step 4)
   */
  const validatePhoneLive = (phone) => {
    const phoneRegex = /^(07|02)\d{8}$/;
    const trimmedPhone = phone.trim();

    if (!trimmedPhone) {
      setPhoneError('');
      return;
    }

    if (!phoneRegex.test(trimmedPhone)) {
      setPhoneError('Format invalid: 07... sau 02... (10 cifre)');
    } else {
      setPhoneError(''); // Valid!
    }
  };

  const handleApplyDiscount = async () => {
    const code = discountCode.trim();
    if (!code) return;
    setDiscountLoading(true);
    setDiscountError('');
    try {
      const res = await fetch(`${API_URL}/api/orders/validate-discount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          certificateType: formData.certificateType,
          email: formData.contactData.email
        })
      });
      const data = await res.json();
      if (data.valid) {
        setAppliedDiscount({ code: data.code, discountAmount: data.discountAmount, newPrice: data.newPrice });
      } else {
        setAppliedDiscount(null);
        setDiscountError(data.error || 'Cod invalid.');
      }
    } catch (err) {
      setAppliedDiscount(null);
      setDiscountError('Eroare la verificarea codului.');
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError('');
    setDiscountDismissed(true);
  };

  // Afișare preț în cardul de la pasul 2: tăiat + redus dacă a venit reducere validă din URL
  const renderCardPrice = (type) => {
    const base = type === 'basic' ? prices.basic : prices.withHistory;
    const d = urlDiscount && urlDiscount[type];
    if (d && d.amount > 0) {
      return (
        <div className="mt-4">
          <p className="text-lg font-semibold text-gray-400 line-through leading-none">{base} RON</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">{d.newPrice} RON</p>
          <span className="inline-block mt-2 bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-1 rounded-full">−{d.amount} lei cu {urlDiscount.code}</span>
        </div>
      );
    }
    return <p className="text-2xl font-bold text-emerald-600 mt-4">{base} RON</p>;
  };

  const handleSubmitOrder = async () => {
    // Log analytics în backend
    const identifier = formData.userType === 'individual' ? 'PF' : (formData.cui || 'UNKNOWN'); // fara CNP in log-uri
    const userType = formData.userType === 'company' ? 'PJ' : 'PF';
    const certType = formData.certificateType === 'basic' ? 'Constatator' : 'Cu Istoric';

    fetch(`${API_URL}/api/orders/analytics/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: `💳 [WIZARD] ${identifier} (${userType}, ${certType}) - Click "Plătește cu cardul"`,
        data: { step: 5, identifier, userType, certType }
      })
    }).catch(() => {}); // Silent fail

    // GA4 funnel: click pe plata (doar analitice)
    const gaType = formData.certificateType;
    const gaDiscount = appliedDiscount ? Number(appliedDiscount.discountAmount) || 0 : 0;
    const gaValue = appliedDiscount
      ? Number(appliedDiscount.newPrice) || 0
      : Number(gaType === 'withHistory' ? prices.withHistory : prices.basic) || 0;
    trackEvent('add_payment_info', {
      currency: 'RON',
      value: gaValue,
      coupon: appliedDiscount ? appliedDiscount.code : undefined,
      items: [buildItem(gaType, gaValue, gaDiscount)],
      user_type: userType
    });

    setIsVerifyingCUI(true);  // Folosim CUI loading pentru submit (generic)
    setToast(null); // Clear orice toast anterior

    try {
      // FIX #27 - Adăugăm acceptedMarketing în formData
      const orderData = {
        ...formData,
        acceptedMarketing: acceptedMarketing,
        discountCode: appliedDiscount ? appliedDiscount.code : undefined,
        attribution: getAttribution(), // Analitice: de unde a venit clientul (curatat pe server)
        draftToken: draftToken || undefined // comanda nefinalizata -> "convertita" (nu mai primeste reamintire)
      };

      const response = await fetch(`${API_URL}/api/orders/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });
      const data = await response.json();

      if (data.success && data.checkoutUrl) {
        // Redirectează către Stripe Checkout
        console.log('Redirectare către Stripe Checkout...');
        // GA4: datele pentru purchase, citite pe pagina de succes (doar analitice)
        savePendingPurchase({
          orderNumber: data.orderNumber,
          certificateType: gaType,
          value: gaValue,
          discount: gaDiscount,
          coupon: appliedDiscount ? appliedDiscount.code : undefined
        });
        window.location.href = data.checkoutUrl;
      } else {
        setToast({ message: data.error || 'Eroare la crearea comenzii', type: 'error' });
        setIsVerifyingCUI(false);
      }
    } catch (err) {
      setToast({ message: 'Eroare la procesarea comenzii: ' + err.message, type: 'error' });
      setIsVerifyingCUI(false);
    }
  };

  const steps = [
    { num: 1, title: 'Date firmă', icon: User },
    { num: 2, title: 'Certificat', icon: FileText },
    { num: 3, title: 'Facturare', icon: Building },
    { num: 4, title: 'Contact', icon: User },
    { num: 5, title: 'Confirmare', icon: CheckCircle }
  ];

  const canProceed = () => {
    switch(step) {
      case 1:
        return formData.userType && ((formData.userType === 'company' && formData.companyData) || (formData.userType === 'individual' && cnpValidated));
      case 2:
        // Pentru withHistory nu cere scop, pentru basic DA
        return formData.certificateType && (formData.certificateType === 'withHistory' || formData.certificatePurpose);
      case 3:
        if (!formData.billingType) return false;
        if (formData.billingType === 'sameCompany') return true;
        if (formData.billingType === 'otherCompany') return formData.billingData.name;
        if (formData.billingType === 'individual') {
          // FIX #27 - Validare minimă bazată pe testare FGO (acceptă orice, dar punem limite UX)
          const name = (formData.billingData.name || '').trim();
          const address = (formData.billingData.address || '').trim();
          return name.length >= 3 && address.length >= 5;
        }
        return false;
      case 4:
        // FIX #27 - Validare Date Contact
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/; // Interzice puncte duble, spații, caractere invalide
        const phoneRegex = /^(07|02)\d{8}$/; // 07... sau 02... (10 cifre total)
        const fullName = (formData.contactData.fullName || '').trim();
        const email = formData.contactData.email || '';
        const phone = (formData.contactData.phone || '').trim();

        // Nume minim 3 caractere
        if (fullName.length < 3) return false;

        // Email valid
        if (!email || !emailRegex.test(email)) return false;

        // Telefon: dacă există, trebuie valid (07... sau 02...)
        if (phone && !phoneRegex.test(phone)) return false;

        return true;
      default:
        return true;
    }
  };

  const scrollToWizard = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setStep(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* JSON-LD (WebSite, LocalBusiness, Service, FAQPage) DOAR pe homepage - src/data/homepage-schema.json */}
      <Helmet>
        <title>Certificat Constatator Online ONRC – 87 lei + TVA, în 5 minute</title>
        <meta name="description" content="Certificat constatator ONRC online, semnat electronic, pe email în 5 minute. 87 lei + TVA, taxa ONRC inclusă, fără taxă de urgență." />
        <meta property="og:title" content="Certificat Constatator Online ONRC – 87 lei + TVA, în 5 minute" />
        <meta property="og:description" content="Certificat constatator ONRC online, semnat electronic, pe email în 5 minute. 87 lei + TVA, taxa ONRC inclusă, fără taxă de urgență." />
        <link rel="canonical" href="https://constatatoare.ro/" />
        <meta property="og:url" content="https://constatatoare.ro/" />
        <script type="application/ld+json">{JSON.stringify(homepageSchema)}</script>
      </Helmet>

      <Header />

      {/* Maintenance Mode Banner */}
      {maintenanceMode && (
        <div className="bg-yellow-500 text-yellow-900 py-4 px-4 text-center font-semibold shadow-lg">
          <span className="inline-flex items-center gap-2"><Construction className="w-5 h-5 flex-shrink-0" />Platformă în mentenanță. Comenzile noi sunt temporar dezactivate. Revenim curând!</span>
        </div>
      )}

      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* H1 pentru SEO - vizibil */}
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-6">
          Certificat Constatator Online <span className="text-emerald-600">în 5 Minute</span>
        </h1>

        {/* Layout: Hero | Wizard | Specimen (3 coloane pe desktop, stack pe mobile) */}
        <div className={`grid grid-cols-1 gap-6 transition-all duration-500 ease-in-out ${
          step === 1
            ? 'lg:grid-cols-[25%_40%_35%]'
            : 'lg:grid-cols-[280px_1fr] lg:max-w-4xl lg:mx-auto'
        }`}>

          {/* HERO - Al doilea pe mobile (order-2), primul pe desktop (lg:order-1) */}
          <div className="order-2 lg:order-1 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl shadow-lg p-6 lg:p-8 border-2 border-emerald-200">
            {/* Tagline mare cu badge "5 minute" */}
            <p className="text-2xl lg:text-3xl font-bold text-gray-900 text-center mb-2">
              Certificat Constatator în
            </p>
            <div className="flex items-center justify-center mb-6">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-blue-50 border-2 border-emerald-600 rounded-2xl shadow-xl">
                <Zap className="w-6 h-6 text-yellow-500 fill-yellow-500" />
                <span className="text-emerald-600 font-bold text-4xl">5 minute</span>
              </div>
            </div>

            {/* 4 Bullet points */}
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm lg:text-base text-gray-800 font-medium">Disponibil 24/7 - rapid și securizat</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm lg:text-base text-gray-800 font-medium">TVA și taxă ONRC inclusă</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm lg:text-base text-gray-800 font-medium">Livrare instant, fără cont</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm lg:text-base text-gray-800 font-medium">Certificat original semnat electronic</span>
              </li>
            </ul>

            {/* Badge-uri Stripe + SSL */}
            <div className="flex items-center justify-center gap-4">
              <img src="/stripe.png" alt="Powered by Stripe" className="h-10 w-auto opacity-80 hover:opacity-100 transition-opacity" width="200" height="200" />
              <img src="/ssl.png" alt="SSL Secure" className="h-10 w-auto opacity-80 hover:opacity-100 transition-opacity" width="867" height="503" />
            </div>
          </div>

          {/* WIZARD - Primul pe mobile (order-1), mijloc pe desktop (lg:order-2) */}
          <div className="order-1 lg:order-2 bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col">
            {/* Banner reducere din URL (?cod= din email): activă (verde) sau cod invalid (amber) */}
            {urlDiscount ? (
              <div className="bg-emerald-50 border-b border-emerald-200 px-4 lg:px-6 py-3 text-center">
                <span className="text-sm font-semibold text-emerald-800 inline-flex items-center gap-1.5 flex-wrap justify-center"><PartyPopper className="w-4 h-4 flex-shrink-0" />Ai o reducere activă: <span className="font-mono font-bold">{urlDiscount.code}</span></span>
              </div>
            ) : urlCodeError ? (
              <div className="bg-amber-50 border-b border-amber-200 px-4 lg:px-6 py-3 text-center">
                <span className="text-sm font-semibold text-amber-800 inline-flex items-center gap-1.5 flex-wrap justify-center"><AlertTriangle className="w-4 h-4 flex-shrink-0" />Codul <span className="font-mono font-bold">{urlCodeError}</span> nu există sau nu mai este valid.</span>
              </div>
            ) : null}
            {/* Progress Bar Orizontal - Vizibil pe ambele (mobile: verde, desktop: alb) */}
            <div ref={progressBarRef} className="bg-emerald-600 lg:bg-white lg:border-b lg:border-gray-200 px-4 lg:px-6 py-4">
              <div className="flex justify-between items-start max-w-lg mx-auto">
                {steps.map((s, idx) => (
                  <React.Fragment key={s.num}>
                    <div className="flex flex-col items-center">
                      <div className={`w-9 lg:w-10 h-9 lg:h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        step >= s.num
                          ? 'bg-white lg:bg-emerald-600 text-emerald-600 lg:text-white'
                          : 'bg-teal-500 lg:bg-gray-200 text-white lg:text-gray-500'
                      }`}>
                        {step > s.num ? <CheckCircle className="w-5 lg:w-6 h-5 lg:h-6" /> : <span className="font-bold text-sm">{s.num}</span>}
                      </div>
                      <span className={`mt-1 text-[10px] lg:text-xs font-medium transition-all duration-300 hidden sm:block text-center whitespace-nowrap ${
                        step === s.num
                          ? 'text-white lg:text-emerald-600 font-semibold'
                          : step > s.num
                          ? 'text-emerald-100 lg:text-gray-500'
                          : 'text-emerald-200 lg:text-gray-400'
                      }`}>
                        {s.title}
                      </span>
                    </div>
                    {idx < steps.length - 1 && (
                      <div className={`h-0.5 flex-1 mx-2 lg:mx-3 mt-[18px] lg:mt-5 transition-all duration-300 ${
                        step > s.num ? 'bg-white lg:bg-emerald-600' : 'bg-teal-500 lg:bg-gray-300'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Form Content */}
            <div className="p-6 lg:p-8">
            {/* Step 1 */}
            {step === 1 && (
              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Persoana pentru care se solicită certificatul</h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-lg mx-auto">
                  <button
                    onClick={() => !maintenanceMode && handleUserTypeChange('company')}
                    disabled={maintenanceMode}
                    className={`p-6 border-2 rounded-xl transition-all duration-200 ${
                      formData.userType === 'company'
                        ? 'border-emerald-600 bg-blue-50 shadow-lg transform scale-105'
                        : 'border-gray-300 hover:border-blue-300 hover:shadow-md'
                    } ${maintenanceMode ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}>
                    <Building className="w-12 h-12 mx-auto mb-3 text-emerald-600" />
                    <h3 className="text-lg font-semibold mb-1 text-center">Persoană Juridică</h3>
                    <p className="text-sm text-gray-600 text-center">Firmă / Societate</p>
                    <p className="text-xs text-gray-500 mt-1 text-center">SRL, SA, PFA, II, IF</p>
                  </button>

                  <button
                    onClick={() => !maintenanceMode && handleUserTypeChange('individual')}
                    disabled={maintenanceMode}
                    className={`p-6 border-2 rounded-xl transition-all duration-200 ${
                      formData.userType === 'individual'
                        ? 'border-emerald-600 bg-blue-50 shadow-lg transform scale-105'
                        : 'border-gray-300 hover:border-blue-300 hover:shadow-md'
                    } ${maintenanceMode ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}>
                    <User className="w-12 h-12 mx-auto mb-3 text-emerald-600" />
                    <h3 className="text-lg font-semibold mb-1 text-center">Persoană Fizică</h3>
                    <p className="text-sm text-gray-600 text-center">CNP</p>
                    <p className="text-xs text-gray-500 mt-1 text-center">Istoric: administrator, asociat</p>
                  </button>
                </div>

                {formData.userType === 'company' && (
                  <div className="mt-8 max-w-lg mx-auto">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">Introduceți CUI</label>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        id="cui-input"
                        type="text"
                        value={formData.cui}
                        onChange={(e) => setFormData({ ...formData, cui: e.target.value })}
                        onKeyPress={(e) => e.key === 'Enter' && handleVerifyCUI()}
                        disabled={maintenanceMode}
                        className={`flex-1 px-4 py-3 h-12 text-base border-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all ${
                          flashElement === 'cui-input' ? 'animate-gentle-flash' : 'border-gray-300'
                        }`}
                        placeholder="RO12345678 sau 12345678"
                      />
                      <button
                        onClick={() => handleVerifyCUI()}
                        disabled={isVerifyingCUI || !formData.cui || maintenanceMode}
                        className="px-6 sm:px-8 py-3 min-h-[48px] bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap">
                        {isVerifyingCUI ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Se verifică...
                          </>
                        ) : (
                          'Caută firmă'
                        )}
                      </button>
                    </div>

                    {formData.companyData && (
                      <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-gray-800">
                            <p className="font-semibold">{formData.companyData.name}</p>
                            <p className="text-gray-600 mt-1">
                              CUI: {formData.companyData.cui}, Nr. Reg. Com.: {formData.companyData.registrationNumber}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {formData.userType === 'individual' && (
                  <div className="mt-8">
                    {resumeNeedsCnp && (
                      <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-900">
                        <strong>Bine ați revenit!</strong> Datele comenzii sunt completate. Din motive de siguranță nu păstrăm
                        CNP-ul — reintroduceți-l și apăsați „Caută CNP", apoi vă ducem direct la confirmare.
                      </div>
                    )}
                    <label className="block text-sm font-semibold text-gray-700 mb-3">Introduceți CNP</label>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        id="cnp-input"
                        type="text"
                        value={formData.cnp}
                        onChange={(e) => {
                          setFormData({ ...formData, cnp: e.target.value });
                          setCnpValidated(false); // Reset validare când se schimbă CNP
                        }}
                        onKeyPress={(e) => e.key === 'Enter' && handleVerifyCNP()}
                        disabled={maintenanceMode}
                        className={`flex-1 px-4 py-3 h-12 text-base border-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all ${
                          flashElement === 'cnp-input' ? 'animate-gentle-flash' : 'border-gray-300'
                        }`}
                        placeholder="1234567890123"
                        maxLength="13"
                      />
                      <button
                        onClick={handleVerifyCNP}
                        disabled={isVerifyingCNP || !formData.cnp || maintenanceMode}
                        className="px-6 sm:px-8 py-3 min-h-[48px] bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap">
                        {isVerifyingCNP ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Se verifică...
                          </>
                        ) : (
                          'Caută CNP'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2 - Tip Certificat */}
            {step === 2 && (
              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Alegeți tipul de certificat</h2>

                {/* Pentru persoane fizice - doar Certificat Constatator */}
                {formData.userType === 'individual' ? (
                  <div className="max-w-md mx-auto">
                    <button
                      onClick={() => {
                        setFormData({ ...formData, certificateType: 'basic' });
                        setTimeout(() => triggerFlash('certificate-purpose', 3000), 100);
                        // Mobile: Scroll la butonul Continuare după selectare
                        setTimeout(() => {
                          if (window.innerWidth < 1024 && continueButtonRef.current) {
                            continueButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                        }, 200);
                      }}
                      className={`w-full p-8 border-2 rounded-xl transition-all ${
                        formData.certificateType === 'basic' ? 'border-emerald-600 bg-blue-50 shadow-lg' : 'border-gray-300 hover:border-blue-300'
                      }`}>
                      <FileText className="w-16 h-16 mx-auto mb-4 text-emerald-600" />
                      <h3 className="text-xl font-semibold mb-3">Certificat Constatator</h3>
                      <div className="text-sm text-gray-600 space-y-1 text-center">
                        <p className="flex items-center justify-center gap-1.5"><CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />Formă detaliată/extinsă</p>
                        <p className="flex items-center justify-center gap-1.5"><Zap className="w-4 h-4 text-emerald-600 flex-shrink-0" />Livrare în 5 minute</p>
                        <p className="flex items-center justify-center gap-1.5"><ClipboardList className="w-4 h-4 text-emerald-600 flex-shrink-0" />Necesar la: bănci, licitații, credit, leasing, instituții publice</p>
                        <p className="flex items-center justify-center gap-1.5"><Lock className="w-4 h-4 text-emerald-600 flex-shrink-0" />Semnat electronic ONRC</p>
                      </div>
                      {renderCardPrice('basic')}
                      <p className="text-xs text-gray-500">TVA și taxă ONRC incluse</p>
                    </button>
                  </div>
                ) : (
                  /* Pentru persoane juridice - ambele opțiuni */
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
                    <button
                      onClick={() => {
                        setFormData({ ...formData, certificateType: 'basic' });
                        setTimeout(() => triggerFlash('certificate-purpose', 3000), 100);
                        // Mobile: Scroll la butonul Continuare după selectare
                        setTimeout(() => {
                          if (window.innerWidth < 1024 && continueButtonRef.current) {
                            continueButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                        }, 200);
                      }}
                      className={`p-8 border-2 rounded-xl transition-all relative ${
                        formData.certificateType === 'basic' ? 'border-emerald-600 bg-blue-50 shadow-lg' : 'border-gray-300 hover:border-blue-300'
                      }`}>
                      {/* Badge "Cel mai popular" */}
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md whitespace-nowrap">
                          <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-current" />Cel mai popular</span>
                        </span>
                      </div>
                      <FileText className="w-16 h-16 mx-auto mb-4 text-emerald-600" />
                      <h3 className="text-xl font-semibold mb-3 text-center">Certificat Constatator</h3>
                      <div className="text-sm text-gray-600 space-y-1 text-center">
                        <p className="flex items-center justify-center gap-1.5"><CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />Formă detaliată/extinsă</p>
                        <p className="flex items-center justify-center gap-1.5"><Zap className="w-4 h-4 text-emerald-600 flex-shrink-0" />Livrare în 5 minute</p>
                        <p className="flex items-center justify-center gap-1.5"><ClipboardList className="w-4 h-4 text-emerald-600 flex-shrink-0" />Necesar la: bănci, licitații, credit, leasing, instituții publice</p>
                        <p className="flex items-center justify-center gap-1.5"><Lock className="w-4 h-4 text-emerald-600 flex-shrink-0" />Semnat electronic ONRC</p>
                      </div>
                      {renderCardPrice('basic')}
                      <p className="text-xs text-gray-500">TVA și taxă ONRC incluse</p>
                    </button>

                    <button
                      onClick={() => {
                        setFormData({ ...formData, certificateType: 'withHistory' });
                        // Mobile: Scroll la butonul Continuare după selectare
                        setTimeout(() => {
                          if (window.innerWidth < 1024 && continueButtonRef.current) {
                            continueButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                        }, 200);
                      }}
                      className={`p-8 border-2 rounded-xl transition-all ${
                        formData.certificateType === 'withHistory' ? 'border-emerald-600 bg-blue-50 shadow-lg' : 'border-gray-300 hover:border-blue-300'
                      }`}>
                      <FileText className="w-16 h-16 mx-auto mb-4 text-emerald-600" />
                      <h3 className="text-xl font-semibold mb-3 text-center">Certificat cu Istoric</h3>
                      <div className="text-sm text-gray-600 space-y-1 text-center">
                        <p className="flex items-center justify-center gap-1.5"><CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />Toate înregistrările din firmă</p>
                        <p className="flex items-center justify-center gap-1.5"><Zap className="w-4 h-4 text-emerald-600 flex-shrink-0" />Livrare în 5 minute</p>
                        <p className="flex items-center justify-center gap-1.5"><Calendar className="w-4 h-4 text-emerald-600 flex-shrink-0" />Situația completă la zi</p>
                        <p className="flex items-center justify-center gap-1.5"><Lock className="w-4 h-4 text-emerald-600 flex-shrink-0" />Semnat electronic ONRC</p>
                      </div>
                      {renderCardPrice('withHistory')}
                      <p className="text-xs text-gray-500">TVA și taxă ONRC incluse</p>
                    </button>
                  </div>
                )}

                {/* Lista scopuri DOAR pentru Certificat Constatator */}
                {formData.certificateType === 'basic' && (
                  <div className="mt-8">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Alegeți scopul certificatului constatator *
                    </label>
                    <select
                      id="certificate-purpose"
                      value={formData.certificatePurpose}
                      onChange={(e) => setFormData({ ...formData, certificatePurpose: e.target.value })}
                      className={`w-full px-4 py-3 h-12 text-base border-2 rounded-lg focus:ring-2 focus:ring-teal-500 transition-all ${
                        flashElement === 'certificate-purpose' ? 'animate-gentle-flash' : 'border-gray-300'
                      }`}>
                      <option value="">Selectați...</option>
                      {(formData.userType === 'individual' ? purposesIndividual : purposesCompany).map(purpose => (
                        <option key={purpose} value={purpose}>{purpose}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Step 3 - ACTUALIZAT CU LISTAFIRME API */}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Alegeți datele de facturare</h2>
                
                <div className="space-y-4">
                  {/* Opțiunea 1: Firma din constatator */}
                  <button
                    onClick={() => setFormData({ 
                      ...formData, 
                      billingType: 'sameCompany',
                      billingData: formData.companyData ? {
                        type: 'company',
                        name: formData.companyData.name,
                        cui: formData.companyData.cui,
                        registrationNumber: formData.companyData.registrationNumber,
                        address: formData.companyData.address,
                        county: formData.companyData.county,
                        city: formData.companyData.city
                      } : {}
                    })}
                    disabled={!formData.companyData}
                    className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                      formData.billingType === 'sameCompany' 
                        ? 'border-emerald-600 bg-blue-50' 
                        : 'border-gray-300 hover:border-blue-300'
                    } ${!formData.companyData ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <h3 className="font-semibold">Eliberare factură pe firma din constatator</h3>
                    <p className="text-sm text-gray-600">Date preluate automat</p>
                  </button>

                  {formData.billingType === 'sameCompany' && formData.companyData && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-gray-700"><strong>Firmă:</strong> {formData.companyData.name}</p>
                      <p className="text-sm text-gray-700"><strong>CUI:</strong> {formData.companyData.cui}</p>
                      <p className="text-sm text-gray-700"><strong>Adresă:</strong> {formData.companyData.address}</p>
                    </div>
                  )}

                  {/* Opțiunea 2: Altă firmă */}
                  <button
                    onClick={() => {
                      setFormData({ ...formData, billingType: 'otherCompany', billingData: {} });
                      setTimeout(() => triggerFlash('billing-cui-input'), 100);
                    }}
                    className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                      formData.billingType === 'otherCompany' ? 'border-emerald-600 bg-blue-50' : 'border-gray-300 hover:border-blue-300'
                    }`}>
                    <h3 className="font-semibold">Eliberare factură pe altă firmă</h3>
                    <p className="text-sm text-gray-600">Introduceți CUI</p>
                  </button>

                  {formData.billingType === 'otherCompany' && (
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">CUI Firmă pentru facturare</label>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <input
                            id="billing-cui-input"
                            type="text"
                            value={formData.billingData.cui || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              billingData: { ...formData.billingData, cui: e.target.value }
                            })}
                            onKeyPress={(e) => e.key === 'Enter' && handleVerifyCUI(formData.billingData.cui, true)}
                            className={`flex-1 px-4 py-3 h-12 text-base border-2 rounded-lg focus:ring-2 focus:ring-teal-500 transition-all ${
                              flashElement === 'billing-cui-input' ? 'animate-gentle-flash' : 'border-gray-300'
                            }`}
                            placeholder="RO87654321"
                          />
                          <button
                            onClick={() => handleVerifyCUI(formData.billingData.cui, true)}
                            disabled={isVerifyingCUI || !formData.billingData.cui}
                            className="px-6 sm:px-8 py-3 min-h-[48px] bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 font-semibold flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap">
                            {isVerifyingCUI ? (
                              <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Se verifică...
                              </>
                            ) : (
                              'Caută firmă'
                            )}
                          </button>
                        </div>
                      </div>

                      {formData.billingData.name && (
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <h4 className="font-semibold text-green-900 mb-2 flex items-center">
                            <CheckCircle className="w-5 h-5 mr-2" />
                            Date firmă pentru facturare:
                          </h4>
                          <div className="space-y-1 text-sm">
                            <p><strong>Nume:</strong> {formData.billingData.name}</p>
                            <p><strong>CUI:</strong> {formData.billingData.cui}</p>
                            <p><strong>Nr. Reg. Com.:</strong> {formData.billingData.registrationNumber}</p>
                            <p><strong>Adresă:</strong> {formData.billingData.address}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Opțiunea 3: Persoană fizică */}
                  <button
                    onClick={() => {
                      setFormData({
                        ...formData,
                        billingType: 'individual',
                        billingData: { type: 'individual' }
                      });
                      // Flash secvențial: Nume (100ms) → Adresă (900ms = 100 + 800)
                      setTimeout(() => triggerFlash('billing-name-input'), 100);
                      setTimeout(() => triggerFlash('billing-address-input'), 900);
                    }}
                    className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                      formData.billingType === 'individual' ? 'border-emerald-600 bg-blue-50' : 'border-gray-300 hover:border-blue-300'
                    }`}>
                    <h3 className="font-semibold">Eliberare factură pe persoană fizică</h3>
                    <p className="text-sm text-gray-600">Introduceți nume și prenume</p>
                  </button>

                  {formData.billingType === 'individual' && (
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Nume și Prenume</label>
                        <input
                          id="billing-name-input"
                          type="text"
                          value={formData.billingData.name || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            billingData: { ...formData.billingData, type: 'individual', name: e.target.value }
                          })}
                          className={`w-full px-4 py-3 h-12 text-base border-2 rounded-lg focus:ring-2 focus:ring-teal-500 transition-all ${
                            flashElement === 'billing-name-input' ? 'animate-gentle-flash' : 'border-gray-300'
                          }`}
                          placeholder="Ion Popescu"
                        />
                        <p className="text-xs text-gray-500 mt-1">Minim 3 caractere</p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Adresă</label>
                        <input
                          id="billing-address-input"
                          type="text"
                          value={formData.billingData.address || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            billingData: { ...formData.billingData, address: e.target.value }
                          })}
                          className={`w-full px-4 py-3 h-12 text-base border-2 rounded-lg focus:ring-2 focus:ring-teal-500 transition-all ${
                            flashElement === 'billing-address-input' ? 'animate-gentle-flash' : 'border-gray-300'
                          }`}
                          placeholder="Str. Exemplu Nr. 1, București"
                        />
                        <p className="text-xs text-gray-500 mt-1">Format recomandat: Str. Mihai Viteazu, Nr. 10, București (minim 5 caractere)</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 4 - Unchanged */}
            {step === 4 && (
              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Unde trimitem factura și certificatul?</h2>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nume și Prenume *</label>
                  <input
                    id="contact-name-input"
                    type="text"
                    value={formData.contactData.fullName}
                    onChange={(e) => {
                      const newName = e.target.value;
                      setFormData({
                        ...formData,
                        contactData: { ...formData.contactData, fullName: newName }
                      });
                      validateNameLive(newName);
                    }}
                    className={`w-full px-4 py-3 h-12 text-base border-2 rounded-lg transition-all ${
                      flashElement === 'contact-name-input' ? 'animate-gentle-flash' :
                      formData.contactData.fullName && nameError ? 'border-red-500 bg-red-50 focus:ring-red-500' :
                      formData.contactData.fullName && !nameError && formData.contactData.fullName.trim().length >= 3 ? 'border-green-500 bg-green-50 focus:ring-green-500' :
                      'border-gray-300 focus:ring-teal-500'
                    }`}
                    placeholder="Ion Popescu"
                  />
                  {formData.contactData.fullName && nameError ? (
                    <p className="text-xs text-red-600 mt-1 font-medium flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />{nameError}</p>
                  ) : formData.contactData.fullName && !nameError && formData.contactData.fullName.trim().split(/\s+/).filter(w => w.length > 0).length >= 2 ? (
                    <p className="text-xs text-green-600 mt-1 font-medium flex items-center gap-1"><Check className="w-3.5 h-3.5 flex-shrink-0" />Nume valid</p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">Introduceți Nume și Prenume (fiecare cuvânt minim 3 caractere)</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                  <input
                    id="contact-email-input"
                    type="email"
                    value={formData.contactData.email}
                    onChange={(e) => {
                      const newEmail = e.target.value;
                      setFormData({
                        ...formData,
                        contactData: { ...formData.contactData, email: newEmail }
                      });
                      validateEmailLive(newEmail);
                    }}
                    className={`w-full px-4 py-3 h-12 text-base border-2 rounded-lg transition-all ${
                      flashElement === 'contact-email-input' ? 'animate-gentle-flash' :
                      formData.contactData.email && emailError ? 'border-red-500 bg-red-50 focus:ring-red-500' :
                      formData.contactData.email && !emailError ? 'border-green-500 bg-green-50 focus:ring-green-500' :
                      'border-gray-300 focus:ring-teal-500'
                    }`}
                    placeholder="ion.popescu@email.ro"
                  />
                  {formData.contactData.email && emailError ? (
                    <p className="text-xs text-red-600 mt-1 font-medium flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />{emailError}</p>
                  ) : formData.contactData.email && !emailError ? (
                    <p className="text-xs text-green-600 mt-1 font-medium flex items-center gap-1"><Check className="w-3.5 h-3.5 flex-shrink-0" />Email valid</p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">Format valid: nume@domeniu.ro</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Telefon (opțional)</label>
                  <input
                    type="tel"
                    value={formData.contactData.phone}
                    onChange={(e) => {
                      const newPhone = e.target.value;
                      setFormData({
                        ...formData,
                        contactData: { ...formData.contactData, phone: newPhone }
                      });
                      validatePhoneLive(newPhone);
                    }}
                    className={`w-full px-4 py-3 h-12 text-base border-2 rounded-lg transition-all ${
                      formData.contactData.phone && phoneError ? 'border-red-500 bg-red-50 focus:ring-red-500' :
                      formData.contactData.phone && !phoneError ? 'border-green-500 bg-green-50 focus:ring-green-500' :
                      'border-gray-300 focus:ring-teal-500'
                    }`}
                    placeholder="0721234567"
                  />
                  {formData.contactData.phone && phoneError ? (
                    <p className="text-xs text-red-600 mt-1 font-medium flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />{phoneError}</p>
                  ) : formData.contactData.phone && !phoneError ? (
                    <p className="text-xs text-green-600 mt-1 font-medium flex items-center gap-1"><Check className="w-3.5 h-3.5 flex-shrink-0" />Telefon valid</p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">Opțional - Primiți notificare SMS când certificatul este gata (format: 07...)</p>
                  )}
                </div>
              </div>
            )}

            {/* Step 5 - Unchanged */}
            {step === 5 && (
              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Confirmare comandă</h2>
                
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
                  <p className="text-center text-sm text-gray-600 mb-1">Total comandă:</p>
                  {appliedDiscount ? (
                    <>
                      <div className="text-center mb-1">
                        <span className="block text-lg font-semibold text-gray-400 line-through">
                          {formData.certificateType === 'basic' ? prices.basic : prices.withHistory} RON
                        </span>
                        <span className="block text-4xl font-bold text-blue-900">{appliedDiscount.newPrice} RON</span>
                      </div>
                      <p className="text-center text-sm text-emerald-700 font-medium">
                        Cod {appliedDiscount.code} aplicat: −{appliedDiscount.discountAmount} RON
                      </p>
                    </>
                  ) : (
                    <h3 className="text-4xl font-bold text-center text-blue-900 mb-1">
                      {formData.certificateType === 'basic' ? prices.basic : prices.withHistory} RON
                    </h3>
                  )}
                  <p className="text-center text-sm text-gray-600">- taxa ONRC inclusă -</p>
                </div>

                {/* Cod de reducere */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ai un cod de reducere?</label>
                  {appliedDiscount ? (
                    <div className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
                      <span className="text-sm text-emerald-800 font-medium">
                        <Check className="w-4 h-4 inline -mt-0.5 mr-1" />Cod <span className="font-mono font-bold">{appliedDiscount.code}</span> aplicat
                      </span>
                      <button type="button" onClick={handleRemoveDiscount}
                        className="text-sm text-gray-500 hover:text-red-600 font-medium flex-shrink-0">
                        Elimină
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          value={discountCode}
                          onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setDiscountError(''); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyDiscount(); } }}
                          placeholder="Introdu codul"
                          className="flex-1 px-4 py-3 min-h-[48px] border-2 border-gray-300 rounded-lg text-sm uppercase focus:ring-2 focus:ring-teal-500 focus:border-emerald-500"
                        />
                        <button
                          type="button"
                          onClick={handleApplyDiscount}
                          disabled={discountLoading || !discountCode.trim()}
                          className="w-full sm:w-auto px-6 py-3 min-h-[48px] bg-emerald-600 text-white rounded-lg font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50">
                          {discountLoading ? 'Se verifică...' : 'Aplică'}
                        </button>
                      </div>
                      {discountError && <p className="text-sm text-red-600 mt-2">{discountError}</p>}
                    </>
                  )}
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="font-semibold mb-4">Detalii comandă:</h4>

                  <div className="space-y-3 text-sm">
                    <div className="py-2 border-b">
                      <p className="text-gray-500 text-xs mb-1">Tip utilizator</p>
                      <p className="font-medium text-gray-900">
                        {formData.userType === 'company' ? 'Persoană Juridică' : 'Persoană Fizică'}
                      </p>
                    </div>

                    {formData.userType === 'company' && formData.companyData && (
                      <div className="py-2 border-b">
                        <p className="text-gray-500 text-xs mb-1">Firmă</p>
                        <p className="font-medium text-gray-900">{formData.companyData.name}</p>
                        {(formData.companyData.cui || formData.cui) && (
                          <p className="text-xs text-gray-500 mt-0.5">CUI {formData.companyData.cui || formData.cui}</p>
                        )}
                      </div>
                    )}

                    {formData.userType === 'individual' && formData.cnp && (
                      <div className="py-2 border-b">
                        <p className="text-gray-500 text-xs mb-1">Persoană (CNP)</p>
                        <p className="font-medium text-gray-900">•••••••••{String(formData.cnp).slice(-4)}</p>
                      </div>
                    )}

                    <div className="py-2 border-b">
                      <p className="text-gray-500 text-xs mb-1">Tip certificat</p>
                      <p className="font-medium text-gray-900">
                        {formData.certificateType === 'basic' ? 'Certificat Constatator' : 'Certificat cu Istoric'}
                      </p>
                    </div>

                    {formData.billingData?.name && (
                      <div className="py-2 border-b">
                        <p className="text-gray-500 text-xs mb-1">Factura pe</p>
                        <p className="font-medium text-gray-900">{formData.billingData.name}</p>
                      </div>
                    )}

                    <div className="py-2 border-b">
                      <p className="text-gray-500 text-xs mb-1">Email</p>
                      <p className="font-medium text-gray-900">{formData.contactData.email}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800 text-center">
                    <Check className="w-4 h-4 inline -mt-0.5 mr-1" />Livrare în câteva minute<br/>
                    Solicitările se procesează automat, imediat după confirmarea plății.
                  </p>
                </div>

                {/* FIX #27 - Checkbox-uri Termeni și Marketing */}
                <div className="space-y-4">
                  {/* Checkbox 1 - Termeni și Condiții (OBLIGATORIU) */}
                  <div
                    id="terms-checkbox"
                    className={`flex items-center justify-center gap-3 p-4 rounded-lg border transition-all ${
                      flashElement === 'terms-checkbox' ? 'animate-gentle-flash bg-gray-50 border-gray-300' : 'bg-gray-50 border-gray-300'
                    }`}>
                    <input
                      type="checkbox"
                      id="acceptTerms"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer flex-shrink-0"
                    />
                    <label htmlFor="acceptTerms" className="text-sm text-gray-700 cursor-pointer">
                      Am citit și sunt de acord cu <a href="/termeni-conditii/" target="_blank" className="text-emerald-600 hover:underline font-semibold">Termenii și Condițiile</a> *
                    </label>
                  </div>

                  {/* Checkbox 2 - Comunicări comerciale (OPȚIONAL) */}
                  <div className="flex items-center justify-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <input
                      type="checkbox"
                      id="acceptMarketing"
                      checked={acceptedMarketing}
                      onChange={(e) => setAcceptedMarketing(e.target.checked)}
                      className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer flex-shrink-0"
                    />
                    <label htmlFor="acceptMarketing" className="text-sm text-gray-700 cursor-pointer">
                      Sunt de acord să primesc comunicări comerciale (oferte, noutăți, promoții)
                    </label>
                  </div>

                  {/* Notă finuță sub checkbox-uri */}
                  <p className="text-xs text-gray-500 text-center italic mt-2">
                    * Câmpurile marcate cu steluță sunt obligatorii pentru finalizarea comenzii
                  </p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between mt-8 pt-6 border-t border-gray-200">
              {/* Buton Înapoi */}
              <div>
                {step > 1 && (
                  <button
                    onClick={() => {
                      // Log analytics în backend
                      const identifier = formData.userType === 'individual' ? 'PF' : (formData.cui || 'UNKNOWN'); // fara CNP in log-uri
                      fetch(`${API_URL}/api/orders/analytics/log`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          event: `🔙 [WIZARD] ${identifier} - Înapoi: Step ${step} → ${step - 1}`,
                          data: { identifier, from: step, to: step - 1 }
                        })
                      }).catch(() => {});

                      setStep(step - 1);
                      setToast(null);
                      setAppliedDiscount(null);
                      setDiscountCode('');
                      setDiscountError('');
                    }}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 sm:px-8 py-3 min-h-[48px] border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-semibold text-sm sm:text-base">
                    <ChevronLeft className="w-5 h-5" />
                    Înapoi
                  </button>
                )}
              </div>

              {/* Buton Mai departe / Plătește */}
              <div className="w-full sm:w-auto">
                {step < 5 ? (
                  <button
                    ref={continueButtonRef}
                    onClick={() => {
                      // FIX #27 - Validare explicită cu mesaj de eroare pentru PF (Pas 3)
                      if (step === 3 && formData.billingType === 'individual') {
                        const name = (formData.billingData.name || '').trim();
                        const address = (formData.billingData.address || '').trim();
                        if (name.length < 3) {
                          setToast({ message: 'Nume și prenume trebuie să conțină minim 3 caractere', type: 'error' });
                          return;
                        }
                        if (address.length < 5) {
                          setToast({ message: 'Adresa trebuie să conțină minim 5 caractere (ex: Str. Mihai Viteazu, Nr. 10, București)', type: 'error' });
                          return;
                        }
                      }

                      // FIX #27 - Validare explicită Date Contact (Pas 4)
                      if (step === 4) {
                        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/; // Interzice puncte duble, spații, caractere invalide
                        const phoneRegex = /^(07|02)\d{8}$/;
                        const fullName = (formData.contactData.fullName || '').trim();
                        const email = formData.contactData.email || '';
                        const phone = (formData.contactData.phone || '').trim();

                        if (fullName.length < 3) {
                          setToast({ message: 'Nume și prenume trebuie să conțină minim 3 caractere', type: 'error' });
                          return;
                        }
                        if (!email || !emailRegex.test(email)) {
                          setToast({ message: 'Adresă email invalidă. Verificați dacă nu există puncte duble sau caractere greșite (ex: nume@yahoo.com)', type: 'error' });
                          return;
                        }
                        if (phone && !phoneRegex.test(phone)) {
                          setToast({ message: 'Format telefon invalid. Folosiți format românesc: 07... sau 02... (fără +40)', type: 'error' });
                          return;
                        }
                      }

                      // Log analytics în backend
                      const identifier = formData.userType === 'individual' ? 'PF' : (formData.cui || 'UNKNOWN'); // fara CNP in log-uri
                      const userType = formData.userType === 'company' ? 'PJ' : (formData.userType === 'individual' ? 'PF' : '?');
                      const certType = formData.certificateType === 'basic' ? 'Constatator' : (formData.certificateType === 'withHistory' ? 'Cu Istoric' : '?');

                      fetch(`${API_URL}/api/orders/analytics/log`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          event: `➡️ [WIZARD] ${identifier} (${userType}, ${certType}) - Mai departe: Step ${step} → ${step + 1}`,
                          data: { identifier, userType, certType, from: step, to: step + 1 }
                        })
                      }).catch(() => {});

                      // Comanda nefinalizata: salvam draftul la intrarea in Confirmare (fara CNP, fara preturi).
                      // Fire-and-forget: orice eroare e ignorata, wizard-ul merge normal.
                      if (step === 4 && abandonedCartEnabled) {
                        fetch(`${API_URL}/api/orders/draft`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            token: draftToken || undefined,
                            userType: formData.userType,
                            certificateType: formData.certificateType,
                            certificatePurpose: formData.certificatePurpose,
                            billingType: formData.billingType,
                            cui: formData.userType === 'company' ? formData.cui : undefined,
                            companyData: formData.userType === 'company' ? formData.companyData : undefined,
                            billingData: formData.billingData,
                            contactData: formData.contactData
                          })
                        })
                          .then((r) => (r.ok ? r.json() : null))
                          .then((d) => {
                            if (d && d.token) {
                              setDraftToken(d.token);
                              try { sessionStorage.setItem('order_draft_token', d.token); } catch (e) {}
                            }
                          })
                          .catch(() => {});
                      }

                      // GA4 funnel: pasii wizard-ului (doar analitice)
                      if (step === 1) {
                        trackEvent('view_item_list', { item_list_name: 'Certificate', user_type: userType });
                      } else if (formData.certificateType) {
                        const gaPrice = Number(formData.certificateType === 'withHistory' ? prices.withHistory : prices.basic) || 0;
                        const gaEvent = { 2: 'select_item', 3: 'add_billing_info', 4: 'begin_checkout' }[step];
                        if (gaEvent) {
                          trackEvent(gaEvent, {
                            currency: 'RON',
                            value: gaPrice,
                            item_list_name: 'Certificate',
                            items: [buildItem(formData.certificateType, gaPrice)],
                            user_type: userType
                          });
                        }
                      }

                      setStep(step + 1);
                      setToast(null);

                      // Mobile: Scroll smooth după schimbare step
                      setTimeout(() => {
                        if (window.innerWidth < 1024) { // lg breakpoint
                          // La step 5, scroll la butonul de plată
                          if (step + 1 === 5 && paymentButtonRef.current) {
                            paymentButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          } else if (progressBarRef.current) {
                            progressBarRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }
                      }, 100);
                    }}
                    disabled={!canProceed() || maintenanceMode}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 sm:px-8 py-3 min-h-[48px] bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold text-sm sm:text-base">
                    {maintenanceMode ? 'Mentenanță...' : 'Continuare'}
                    <ChevronRight className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    ref={paymentButtonRef}
                    onClick={() => {
                      // FIX #27 - Verificare Termeni și Condiții înainte de plată
                      if (!acceptedTerms) {
                        setToast({ message: 'Trebuie să acceptați Termenii și Condițiile pentru a continua', type: 'error' });
                        triggerFlash('terms-checkbox');
                        return;
                      }
                      handleSubmitOrder();
                    }}
                    disabled={isVerifyingCUI || maintenanceMode}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 sm:px-8 py-3 min-h-[48px] bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold text-sm sm:text-base">
                    <CreditCard className="w-5 h-5" />
                    {isVerifyingCUI ? 'Se procesează...' : 'Plătește cu card'}
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* Închide Form Content + Wizard */}
          </div>

          {/* SPECIMEN - Ultimul pe mobile (order-3), ultimul pe desktop (lg:order-3) */}
          {/* Pe mobil: vizibil mereu | Pe desktop: dispare după step 1 */}
          <div className={`order-3 lg:order-3 relative rounded-2xl overflow-hidden shadow-lg bg-white
            transition-all duration-500 ease-in-out
            ${step === 1
              ? 'opacity-100 lg:translate-x-0'
              : 'opacity-100 lg:hidden'
            }`}>
            <img
              src="/specimen-certificat.png"
              alt="Exemplu Certificat Constatator ONRC - Document oficial emis de Registrul Comerțului"
              title="Certificat Constatator ONRC - Specimen"
              className="w-full h-auto object-cover scale-105"
              width="797"
              height="1122"
              loading="lazy"
            />
            {/* Overlay SPECIMEN diagonal */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-red-500 font-bold text-4xl lg:text-5xl tracking-widest transform -rotate-45 drop-shadow-lg opacity-70 select-none">
                SPECIMEN
              </span>
            </div>
          </div>

        </div>
        {/* Închide grid layout 3 coloane */}
      </div>

      <Pricing prices={prices} onOrderClick={scrollToWizard} />
      <UseCases />
      <SeoContent />
      <About />
      <FAQ />
      <Footer />

      {/* Toast Notification (floating bottom) */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default CertificateWizard;
